import { useEffect } from "react";

/**
 * Sets <title>, <meta name="description">, and og/twitter tags for the current page.
 * Lightweight, dependency-free. Reverts on unmount.
 */
export default function usePageMeta({ title, description, canonical, ogImage }) {
  useEffect(() => {
    const prev = { title: document.title };
    if (title) document.title = title;

    const apply = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs.create).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      const prevContent = el.getAttribute("content");
      el.setAttribute("content", attrs.content);
      return () => {
        if (created) el.remove();
        else if (prevContent !== null) el.setAttribute("content", prevContent);
      };
    };

    const undoers = [];
    if (description) {
      undoers.push(apply('meta[name="description"]', {
        create: { name: "description" }, content: description,
      }));
      undoers.push(apply('meta[property="og:description"]', {
        create: { property: "og:description" }, content: description,
      }));
      undoers.push(apply('meta[name="twitter:description"]', {
        create: { name: "twitter:description" }, content: description,
      }));
    }
    if (title) {
      undoers.push(apply('meta[property="og:title"]', {
        create: { property: "og:title" }, content: title,
      }));
      undoers.push(apply('meta[name="twitter:title"]', {
        create: { name: "twitter:title" }, content: title,
      }));
    }
    if (ogImage) {
      undoers.push(apply('meta[property="og:image"]', {
        create: { property: "og:image" }, content: ogImage,
      }));
    }

    // canonical
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonicalEl;
    let prevHref = canonicalEl ? canonicalEl.getAttribute("href") : null;
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    }
    // og:url mirrors canonical
    let ogUrlUndo = null;
    if (canonical) {
      ogUrlUndo = apply('meta[property="og:url"]', {
        create: { property: "og:url" }, content: canonical,
      });
    }

    return () => {
      document.title = prev.title;
      undoers.forEach((fn) => fn());
      if (ogUrlUndo) ogUrlUndo();
      if (canonicalEl) {
        if (canonicalCreated) canonicalEl.remove();
        else if (prevHref !== null) canonicalEl.setAttribute("href", prevHref);
      }
    };
  }, [title, description, canonical, ogImage]);
}
